// ---------------------------------------------------------------------------
// Marketing copy — the tagline / CTA spine + landing section strings.
//
// Sourced from `.design/marketing-lab` (MOTTO_AB winner "F" + the CTA system);
// the motto itself was retired site-wide by the D12 v3 propagation sweep - see
// TAGLINE below. The CTA spine ("Drop your files" / "Start with a sentence" /
// "No signup, no credit card.") is shared across every public surface.
// ---------------------------------------------------------------------------

import { MessagesSquare, type LucideIcon } from 'lucide-react';

import type { TrustpilotReview } from '@/modules/marketing/helpers/trustpilot-proof';
import { STAGE_ICONS } from '@/modules/marketing/content/product/product-links';
import { PRODUCT_STAGES, stageHref } from '@/modules/marketing/content/product/product-stages';

// The single site-wide tagline (D12/D16 propagation, Luka v3): used wherever a
// shelf label is expected - the bottom-CTA eyebrow, the marketing footer
// tagline, the sign-in shell tagline. Distinct from the landing-only hero H1
// (HERO_FILM_H1) and from CATEGORY_LINE below, which is now reserved for
// explanatory body copy.
// Shared with apps/web's sign-in shell, so the string itself lives in
// `@naumu/shared/data/brand-copy` and is re-exported here.
export { TAGLINE } from '@naumu/shared/data/brand-copy';

// The B2C motto ("Your second brain, minus the part you quit.") retired
// site-wide with the v3 propagation sweep (D12) - no surface renders it
// anymore (the personal-kb use-case page keeps the line in its own hero/CTA
// copy directly, not via this constant), so it's deleted rather than kept
// unused. See .design/LANDING_OVERHAUL.md D12.

// The category line - Naumu's mechanism phrase ("working memory"). No longer
// used as a shelf label/tagline (see TAGLINE above) - reserved for
// explanatory body copy that wants the mechanism spelled out. Currently
// unused as a direct render call site after the D12 sweep; kept defined for
// future body copy.
export const CATEGORY_LINE = "Your company's working memory.";

// The coined catchphrase. Still rendered as the tagline on the docs + small
// marketing footers via <Catchphrase/> (the JSX form wraps "filing tax" in the
// dictionary tooltip); plain-string contexts (SEO, manifest) use this constant.
export const CATCHPHRASE = 'Skip the filing tax.';

// One CTA frame across the site (W7 F8): "Get started" is the verb, and the
// file-first entry reads "Add your files" - never "Drop"/"Dump" (the council's
// SMB reader parses those as delete/garbage). "Start with a sentence" is retired.
export const CTA_PRIMARY = 'Add your files';
export const CTA_SECONDARY = 'Get started';
export { CTA_REASSURANCE } from '@naumu/shared/data/start-copy';

// Header actions reuse the existing landing strings for consistency.
export const HEADER_TRY = 'Get started';
// The status line on /start while guest provisioning is in flight. The CTA
// BUTTONS no longer swap their label for this - the DS Button `loading` prop
// keeps the resting label in place (hidden at opacity 0, so the button never
// changes width) and centres a spinner over it. Only the full-page /start state,
// which has nothing else to say, spells the wait out in words.
export { HEADER_TRY_PENDING } from '@naumu/shared/data/start-copy';
export const HEADER_SIGN_IN = 'Sign in';
// Progressive-enhancement target for the Get started button: it is a real link,
// so middle-click / preview / no-JS all lead somewhere that gets you a space.
// With JS the click is intercepted and provisions in place instead. /start runs
// the SAME guest provisioning on mount (app/start/page.tsx -> StartProvision),
// so a followed link behaves exactly like a clicked button and never shows a
// sign-in wall - which would contradict the "No signup, no credit card." line under it.
export const HEADER_TRY_HREF = '/start';

// Footer copyright helper (kept for reuse; footers render the year inline).
export const FOOTER_COPYRIGHT = (year: number) => `© ${year} Naumu`;

// ---------------------------------------------------------------------------
// Landing v2 — section copy (B2B repositioning: "your company's working
// memory"). Hero / philosophy ("what it is") / how you use it / sticky steps
// ("skip the filing tax") / integrations on-ramp / who it's for (company
// personas) / company-objection FAQ / repeated CTA. The working text input
// lives in the hero and the bottom CTA.
// ---------------------------------------------------------------------------

// After a file is dropped/picked, the hero drop-zone becomes a composer
// prefilled with this message; the user submits it to start their space.
export const HERO_FILE_PREFILL = 'Please analyze this file';
// Plural variant — used when more than one file is attached (the filenames
// are visible in the composer's attachment chips, so they're not repeated here).
export const HERO_FILE_PREFILL_PLURAL = 'Please analyze these files';

// Marketing-scoped composer placeholder content for the hero + bottom CTA.
// Replaces the app's PLACEHOLDER_SUGGESTIONS (which stays untouched for the
// real onboarding). Cycles in the composer's typewriter.
export const LANDING_PLACEHOLDER_SUGGESTIONS = [
	'Kickoff with Meridian is Thursday - Ana owns onboarding',
	'Pause the Klarwerk upsell until v2.1 ships',
	'New lead from the conference - intro call Friday',
	'Q3 roadmap: three features, two owners, one deadline',
] as const;

// ---------------------------------------------------------------------------
// Hero film (D4) + hero copy (D5, Luka v3). The compact narrated cut (46.6s).
// The loop strips the audio track and re-encodes to ~650kbps (3.3MB,
// autoplay-safe); the full cut carries the sound and only ever plays from the
// lightbox. All URLs are immutable, content-hashed CDN objects.
// ---------------------------------------------------------------------------
export const HERO_FILM_LOOP_SRC =
	'https://cdn.naumu.ai/media/film/working-memory-film-compact-loop-9bd49da7.mp4';
export const HERO_FILM_FULL_SRC =
	'https://cdn.naumu.ai/media/film/working-memory-film-compact-full-5ba99954.mp4';
// SRT for the muted loop's narration - rendered as placard-style captions over
// the loop (HeroFilmCaptions). Same CDN object family as the film, but fetched
// same-origin through the /cdn-media rewrite in next.config.ts because the R2
// bucket serves no CORS headers for browser fetch().
export const HERO_FILM_CAPTIONS_SRC =
	'/cdn-media/media/film/working-memory-film-compact-captions-7ae78995.srt';
// WebVTT twin of the SRT above, for the `<track>`-based lightbox player
// (VideoPlayer parses VTT only). The hero loop's HeroFilmCaptions keeps
// reading the SRT - it does its own parsing and never touches a `<track>`.
// Same-origin through /cdn-media for the same no-CORS reason.
export const HERO_FILM_CAPTIONS_VTT_SRC =
	'/cdn-media/media/film-captions-vtt/working-memory-film-compact-captions-5fac5a3f.vtt';

// ---------------------------------------------------------------------------
// Product demo (the 53s walkthrough). Formerly the YouTube embed SxC9TL-fn8M,
// now our own CDN objects played through VideoPlayer: the comparison and
// use-case heroes, and every "how it works" step without its own recording.
// Published with `node packages/shot/publish.mjs --file` under the manifest
// slugs product-demo-video / product-demo-poster - immutable, content-hashed.
// ---------------------------------------------------------------------------
export const PRODUCT_DEMO_VIDEO_SRC =
	'https://cdn.naumu.ai/media/product-demo-video/product-demo-4926716a.mp4';
export const PRODUCT_DEMO_POSTER_SRC =
	'https://cdn.naumu.ai/media/product-demo-poster/product-demo-poster-b5ef896f.jpg';

export const HERO_FILM_H1 = 'Team chat that remembers.';
// G4 thinned subhead: sits on the hero's baseline row at max ~560px, so it
// carries no clause it doesn't need.
export const HERO_FILM_SUBHEAD =
	'Talk like you already do. Naumu keeps every task, client, and decision - and follows through.';
// The AI-native reset hero (Luka, 2026-09-16/17, "Group Chat" thread): the
// H1 names the reset; the subhead is the happy sentence with its key words
// bold. Landing2HeroResetLine renders it. The "not managers" notes over the
// bold words, the strike-through corrections, and the flat / loops beats
// were all tried and cut on 2026-09-17 - the plain sentence read best.
// Same day, second take: the H1 became "Going AI-native." and the sentence
// became the tightest definition of an AI-native team - two causes (open,
// one memory) and their effect (small-team speed at any size).
export const HERO_RESET_H1 = 'Going AI-native.';
/** A bold key word in the reset sentence. */
export interface HeroResetWord {
	word: string;
}
// One beat never wraps: "Compound one memory." stays on one row. The last
// sentence is two beats so a phone may break it before "at any size.".
export interface HeroResetBeat {
	parts: (string | HeroResetWord)[];
}
export const HERO_RESET_BEATS: HeroResetBeat[] = [
	// Was "Work in the open." - readers did not know what the idiom meant
	// (feedback, 2026-09-17), so it now names the thing that is open.
	{ parts: ['Keep every conversation ', { word: 'open' }, '.'] },
	{ parts: ['Compound ', { word: 'one memory' }, '.'] },
	{ parts: ['Move like a ', { word: 'small team' }] },
	{ parts: ['at any size.'] },
];
// A small "Naumu is the team chat that makes this the default." line under
// the sentence was tried and cut the same day (Luka) - the hero stays the
// H1 plus the one sentence; the tooling clue lives in Landing2AiNative.
// The film beat's resting frame (W8: the loop must never rest on empty black
// while it buffers). A real frame from the loop itself, extracted at 36.5s
// (the memory-galaxy shot of the compact cut).
export const HERO_FILM_POSTER = '/marketing/assets/images/film/working-memory-film-poster.jpg';
export const HERO_COMPOSER_PLACEHOLDER = 'What are you working on?';
export const HERO_NO_SIGNUP_LINE =
	"No signup - type one message, or paste in everything you've got.";
// D5's typewriter line. The hero passes NO static placeholder (that would
// disable the typewriter entirely), so this rotation is live: the D5 anchor
// line leads, the paste-your-website line rides along.
export const HERO_PASTE_SITE_PLACEHOLDER =
	"Paste your website - Naumu builds your company's memory from it";
export const HERO_PLACEHOLDER_SUGGESTIONS = [
	HERO_COMPOSER_PLACEHOLDER,
	...LANDING_PLACEHOLDER_SUGGESTIONS,
	HERO_PASTE_SITE_PLACEHOLDER,
] as const;

// The landing's post-ledger two-beat origin story: disconnected tool copies
// become the one living graph Naumu builds from them.
export const WHY_NOW_BEAT_1_HEADING =
	'Every tool you use keeps its own version of the truth.';
export const WHY_NOW_BEAT_1_BODY =
	"A date changes in chat. Your ticket still shows the old one, your CRM a third. Whichever copy you trust is the one that's already stale.";
export const WHY_NOW_BEAT_2_HEADING = 'It all becomes one living memory.';
export const WHY_NOW_BEAT_2_BODY =
	'Naumu turns the copies into one living graph. Features, deals and people stay connected, and when a fact changes in conversation, every branch that depends on it moves with it.';
// Beat 3: the same graph, now worked on by the viewer's own agents. The chat
// lines are scripted - the rest of the script (what the agent reads and writes,
// and when) lives in graph/why-now-agent-beat.ts.
// The ledger above already says "Bring your agents in.", so this beat names
// what they get instead of repeating the invitation.
export const WHY_NOW_BEAT_3_HEADING = 'Your agents enjoy the context.';
export const WHY_NOW_BEAT_3_BODY =
	'Tag Claude Code, Cursor or any agent right in the thread. It reads the same memory your team does, gets the work done, and writes back what changed - so memory is current before anyone asks.';
export { WHY_NOW_AGENT_NAME } from '@naumu/ui/components/why-now/why-now-agent-beat';

// Sticky mechanic steps. The section heading is rendered as <Catchphrase/>
// ("Skip the filing tax.") in the component, so the filing-tax tooltip term
// lives here now (no heading string constant).
export const STEPS_EYEBROW = 'How it works';
export const STEPS_SUBHEAD =
	"Stop deciding where things go. Add it and Naumu files it into the graph - placed by meaning, next to what it relates to - so it comes back the moment it's relevant. No folders, no tags, no tidying.";
// The house scripted-disclosure line (W7 F4). The compare + use-case pages have
// carried it under every staged demo since W2 and it is the single most-praised
// credibility move on the site; the landing's own demos say it too, in the same
// wording and the same quiet style.
export const STEPS_SCRIPTED_NOTE =
	'Scripted so you can see the shape - add your own material below and watch it build.';
export const LANDING_STEPS = [
	{
		id: 'add-as-it-comes',
		index: 1,
		title: 'Add it as it comes',
		body: 'Type it, paste it, drag it, talk it out - a Slack thread, a PDF, a link, all of it. You never decide where it goes.',
		tag: 'Kills the blank-page freeze',
	},
	{
		id: 'it-draws-the-map',
		index: 2,
		title: 'It draws the map',
		body: 'It reads what you added, figures out what each thing is, and links it into a connected map. Bring what you already have and it does a first pass you can correct.',
		tag: 'No manual linking',
		// The live, scroll-driven graph that assembles itself (see StickyStepBlock).
		video: { kind: 'graph' },
	},
	{
		id: 'you-just-ask',
		index: 3,
		title: 'You just ask',
		body: 'Ask in plain language - and Naumu answers from the actual connections, surfacing the thing you wrote down months ago before you lose another afternoon to it.',
		tag: 'It already knew',
		// A static exchange with Naumu rendered with the in-app chat surface
		// (see StickyStepBlock → StepChatExchange) instead of a video.
		video: { kind: 'chat' },
	},
] as const;

// Integrations (7A) — defaults drive the standalone /integrations page.
export const INTEGRATIONS_EYEBROW = 'Integrations';
export const INTEGRATIONS_HEADING = 'Bring everything you already use.';
export const INTEGRATIONS_SUBHEAD =
	'Naumu pulls from the apps you live in and folds them into one place you can ask.';

// Landing-only overrides for the integrations band (a soft "on-ramp" framing).
// Passed as props by the landing page (landing2-integrations) so the standalone
// /integrations page keeps the INTEGRATIONS_* defaults above.
export const LANDING_INTEGRATIONS_HEADING = 'Not ready to move your work in yet?';
export const LANDING_INTEGRATIONS_SUBHEAD =
	"Keep working where you are. Naumu starts learning your work in the background. You move in when you're ready.";

export const INTEGRATIONS_CONTACT_PROMPT = 'Anything missing?';
export const INTEGRATIONS_CONTACT_CTA = 'Contact us';
export const INTEGRATIONS_CONTACT_EMAIL = 'nino@naumu.ai';
export const INTEGRATIONS_CONTACT_SUBJECT = 'Missing integration';
export const INTEGRATIONS_CONTACT_BODY =
	"Hi Nino,\n\nI'd love to use Naumu with - (which tool?). Any chance it's on the roadmap?\n\nThanks!";
// `icon` keys map to brand-icons.tsx exports (or special 'mcp' / 'more' tiles).
// `href` points each tile at its connector doc. Notion has NO live connector
// (export-paste only), so its tile points at the honest surface we do have -
// the /compare/notion page - never at a generic /docs "setup guide" claim.
// Used by the standalone /integrations page only; the landing band
// (landing2-integrations) renders its own decorative mosaic roster instead.
export const INTEGRATION_TILES = [
	{ icon: 'notion', label: 'Notion', href: '/compare/notion' },
	{ icon: 'slack', label: 'Slack', href: '/docs/slack' },
	{ icon: 'teams', label: 'Microsoft Teams', href: '/docs/teams' },
	{ icon: 'jira', label: 'Jira', href: '/docs/jira' },
	{ icon: 'linear', label: 'Linear', href: '/docs/linear' },
	{ icon: 'clickup', label: 'ClickUp', href: '/docs/clickup' },
	{ icon: 'claude', label: 'Claude', href: '/docs/claude-ai' },
	{ icon: 'openai', label: 'ChatGPT', href: '/docs/chatgpt' },
	{ icon: 'mcp', label: 'Any MCP server', href: '/docs/local-mcp' },
	{ icon: 'more', label: 'More every week', href: '/docs' },
] as const;

// Who it's for — company-persona band between integrations and the repeated CTA.
// Cards deep-link to their closest B2B use-case page (/use-cases/teams,
// /use-cases/sales-teams, /use-cases/agencies); the catch-all carries
// `href: null` and renders unlinked. `icon: null` is the icon-less catch-all card.
export const WHO_ITS_FOR_EYEBROW = "Who it's for";
export const WHO_ITS_FOR_HEADING = 'One memory. Every team.';
export const WHO_ITS_FOR_SUBHEAD =
	"Naumu starts with one team's work and grows into the whole company's memory - no big-bang rollout, no migration project.";
export const WHO_ITS_FOR_CARDS = [
	{
		icon: 'Telescope',
		title: 'Founders & leadership',
		body: 'See the whole company without asking for status.',
		href: '/use-cases/teams',
	},
	{
		icon: 'Handshake',
		title: 'Sales & client teams',
		body: 'Every client, deal, and promise - queryable.',
		href: '/use-cases/sales-teams',
	},
	{
		icon: 'Blocks',
		title: 'Product & engineering',
		body: 'Decisions, specs, and tickets that stay connected.',
		href: '/use-cases/teams',
	},
	{
		icon: 'Workflow',
		title: 'Operations',
		body: 'The processes everyone asks you about - answered by the memory.',
		href: '/use-cases/teams',
	},
	{
		icon: 'Briefcase',
		title: 'Agencies & consultancies',
		body: "Every client's history, ready when they call.",
		href: '/use-cases/agencies',
	},
	{
		icon: null,
		title: '…and whatever your company runs on.',
		body: 'Every team keeps its own scattered record. Naumu gives them one shared memory.',
		href: null,
	},
] as const;

// Repeated CTA (bottom) — the working text input again. The eyebrow is the
// CATEGORY_LINE; chips + typewriter placeholders are the marketing-scoped B2B
// set above (so the landing composers never pull the app's onboarding chips).
export const LANDING_CTA_HEADING = 'What are you working on?';
export const LANDING_CTA_SUBHEAD =
	'Whatever it is, add it - Naumu turns it into working memory you can query, and answers when you ask.';

// FAQ — company-voice objection-busters, ordered along the buyer's journey
// (positioning → migration → start → cost → permissions → AI read/write →
// mistakes → data). Every factual claim is sourced from existing repo copy:
// content/use-cases/teams-use-case.ts, config/pricing.ts, and the prior FAQ.
export const LANDING_FAQ_HEADING = "The stuff you're actually wondering.";
export const LANDING_FAQ = [
	{
		question: 'Is this replacing our CRM and project tracker, or living next to them?',
		answer:
			"Next to them first, then up to you. Keep the CRM and tracker your team already uses and connect them - Jira, Linear, ClickUp, Slack, or a Notion export; anything with an MCP server plugs in today, and we build direct connectors on request, usually within a week - and Naumu learns your work in the background while nothing changes for the team. Its views can carry pipeline, tickets, owners and deadlines the way you actually track them, so when you're confident you no longer need an old tool, move in. If you never are, you still keep a memory of everything those tools leave out.",
	},
	{
		question: 'Do we have to migrate everything first?',
		answer:
			"No. Start with one team - put one project's material into the box and watch the map build. Naumu pulls from the apps your team already lives in (Jira, Linear, ClickUp, Slack - or a pasted Notion export) and folds them into the same memory in the background. You move the rest in when you're ready, not before.",
	},
	{
		question: 'How do we start?',
		answer:
			"One message, or one team's files. Put one project's material into the box - no signup, and the space is real from the first message. Watch the map build, keep it, then bring the team in. There's no rollout to schedule and no schema to design first.",
	},
	{
		question: 'What does it cost for a team?',
		answer:
			'Free covers 20 people and 1,500 nodes per space, no card. Team is €32 per seat / month for unlimited people and nodes and 5× the AI; Max is €95. Every product feature ships on every plan - you pay to scale (more AI, bigger files, more people), never to unlock.',
	},
	{
		question: 'Who can see what?',
		answer:
			"Permissions are per-member, and every AI assistant inherits them - a connected assistant acts as the person who connected it and sees only what that person can. Your team's memory lives in your private space, and every answer traces back to its source and the member who added it.",
	},
	{
		question: 'Can our AI assistants read and write it too?',
		answer:
			"Yes - and it's the part no other workspace says back. Connect Claude or ChatGPT over MCP and it works in your space as a specific member: reading the whole memory and writing back to it, so context stops being copied between assistants by hand. Tell ChatGPT something today and whoever opens Claude tomorrow already knows.",
	},
	{
		question: 'What if it files something wrong?',
		answer:
			"Every change is visible and undoable. Naumu applies changes on its own - that's what makes it proactive - and each one is logged with a before/after, attributed to the member or agent that made it, and one tap to put back. Read-only is a setting; revoke an assistant's access anytime.",
	},
	{
		question: 'Where does our data live?',
		answer:
			"Your team's memory lives in your private space in Naumu's cloud - the trade for letting every teammate and every agent reach it from anywhere. Answers are generated by named AI subprocessors, Google Cloud (Vertex AI) and Anthropic, both in the US under Standard Contractual Clauses - the full list is on /security. We never train models on your data, you can export the whole space as JSON anytime, and spaces you start without an account expire after 30 days unless you sign in to keep them.",
	},
] as const;

// ---------------------------------------------------------------------------
// Landing v3 (.design/LANDING_OVERHAUL.md, wave 3). The sections below sit
// between the film hero and the bottom CTA in the D6 order: mental map (D7) →
// five blocks (D8) → sticky steps → integrations → proof (D10) → who it's for →
// real thread (D11) → FAQ → CTA.
// ---------------------------------------------------------------------------

// Mental map (D7) — a visual beat, not a copy section: one eyebrow + one
// heading over the autoplaying five-cluster graph. Nothing else by design.
export const MENTAL_MAP_EYEBROW = 'The shift';
// W7 F6: the old "Five silos collapse into one." was the council's worst line
// (dead metaphor, unearned number, and one reader heard grain towers). The
// replacement names the five tools instead of counting abstractions.
export const MENTAL_MAP_HEADING =
	'Chat, docs, CRM, tickets, wiki. One thing that remembers all five.';

// The five feature blocks - reworked into the G1 spine: the 1.0-5.0 ledger
// entries ARE the site's information architecture, each pointing at its
// product page (route map 5b: chat / memory / meetings / agents / views).
// Copy keeps the D8 substance; 02 Memory carries the import
// material as its sub-line, 04 Agents carries the agent + your-own-AI
// material. Each entry declares its stage slug and its own copy; everything
// else (number, name, href, headline, icon) is pulled from the stage registry.
export interface LandingSpineBlock {
	/** Registry slug - the ONE thing a block declares about its stage. */
	slug: string;
	/** Spine number in the site-wide "1.0" grammar - the pointer-link prefix. */
	spineNum: string;
	/** The stage's one-word name (Linear-spine taxonomy; white lead word). */
	name: string;
	/** The stage's nav icon - same component the Product nav rows render. */
	icon: LucideIcon;
	/** The stage's product page (route map 5b). */
	href: string;
	title: string;
	/**
	 * The words of `title` the ledger sets in weight. Defaults to the stage
	 * `name`; a block overrides it when the accent is a phrase ("Team Chat").
	 */
	accent: string;
	body: string;
	/** Optional quieter second paragraph (02 Memory carries import here). */
	subBody?: string;
}

// Ledger titles, spine numbers, names, hrefs and icons are THE stage's -
// single-sourced from PRODUCT_STAGES (and the nav's STAGE_ICONS) so the ledger,
// the Product nav rows and the product pages' next-stage band can never diverge.
const findStage = (slug: string) => {
	const stage = PRODUCT_STAGES.find((s) => s.slug === slug);
	if (!stage) throw new Error(`FIVE_BLOCKS references unknown product stage: ${slug}`);
	return stage;
};

const spineBlock = (
	slug: string,
	copy: { body: string; subBody?: string; accent?: string },
): LandingSpineBlock => {
	const stage = findStage(slug);
	return {
		slug,
		spineNum: `${stage.index}.0`,
		name: stage.label,
		icon: STAGE_ICONS[slug] ?? MessagesSquare,
		href: stageHref(stage),
		title: stage.headline,
		accent: stage.label,
		...copy,
	};
};

export const FIVE_BLOCKS: readonly LandingSpineBlock[] = [
	spineBlock('chat', {
		accent: 'Team Chat',
		body: "Topics, threads, private threads, mentions, reactions - everything your team's chat does today, Naumu does. The difference: every message becomes memory instead of scrolling away.",
	}),
	spineBlock('memory', {
		body: 'Every task, client, decision, and deadline lands in one connected memory, placed next to what it relates to. Ask months later; the answer brings its source.',
	}),
	spineBlock('meetings', {
		body: 'Add a recording to a thread. Naumu writes up what was said, what was decided, and who owns the next step.',
	}),
	spineBlock('agents', {
		body: 'Everything your team said is memory, so an agent starts already knowing the company. Give it a routine - the weekly client update, the pipeline check - and it runs on schedule, in the space, with sources.',
	}),
	spineBlock('views', {
		body: 'Ask for a note, a timeline, or a board and Naumu builds it from what your team already said. Everyone edits the same one as you watch, and it stays current when a fact changes elsewhere. Nothing to set up, nothing to keep in sync.',
	}),
];

// The on-ramp copy: the OPENING BEAT of the why-now region (StartBeat in
// landing2-why-now, actions in Landing2StartActions), where the pinned fixture
// plays it out - a website, then files, then the tools. This copy WAS the
// memory row's sub-line, where the import material read as one more capability;
// with a button under it, it reads as the invitation it always was. The four named connectors are the ones with shipped connectors
// on /product/integrations - keep the list and that page in step.
export const LANDING_START_CTA_HEADING = 'Start from what you have.';
export const LANDING_START_CTA_BODY =
	'Paste your website, add your files, or connect Jira, Linear, ClickUp and Slack. Naumu reads it and builds your memory from there.';
export const LANDING_START_CTA_LINK_LABEL = 'See all integrations';
export const LANDING_START_CTA_LINK_HREF = '/product/integrations';

// Proof (D10) — a compact two-row band: a metrics strip over a logo strip.
// Pulled from prod Neo4j 2026-08-12 (aggregate-only queries mirroring the
// pitch-numbers definitions: external human messages excl. founders/studio,
// AIDiff count, distinct non-freemail sender domains, users in >=1 space).
// Every value rounds DOWN. Refresh alongside /deploy via pitch-numbers.
// NOT RENDERED (W7 F2). The strip was 5/6 judges' "most fake element" - undated
// numbers, an unjudgeable "memory writes", and 27 companies over 380 seats doing
// damning arithmetic in the reader's head. Kept defined (and refreshable) for a
// future proof band that has dates and a denominator; ProofSection renders it
// nowhere today.
export const PROOF_METRICS: { value: string; label: string }[] = [
	{ value: '23,000+', label: 'Messages remembered' },
	{ value: '15,000+', label: 'Memory writes' },
	{ value: '27', label: 'Companies' },
	{ value: '380+', label: 'Seats' },
];
export const PROOF_LOGOS_LABEL = 'Teams using Naumu';
// Visible caption above the logo strip (landing + pricing). Figure kept separate so the
// component can set it in bold (Linear: "Trusted by more than **40,000** companies").
export const PROOF_LOGOS_CAPTION = { lead: 'Trusted by', figure: '50+', tail: 'companies' };
// Placeholder slots until real logo files land. Dropping the real strip in is a
// one-array edit: add `src` per entry (and the real `name`) and the component
// renders the image instead of the abstract tile. No invented company names.
export const PROOF_LOGOS: { name: string; src?: string }[] = [
	{ name: 'Employa', src: '/brand/customers/employa.svg' },
	{ name: 'TradeLocker', src: '/brand/customers/tradelocker.svg' },
	{ name: 'Swivel Gaming', src: '/brand/customers/swivelgaming.svg' },
	{ name: 'Nersio', src: '/brand/customers/nersio.svg' },
	{ name: 'RankRush', src: '/brand/customers/rankrush.svg' },
	{ name: 'Props Madness', src: '/brand/customers/propsmadness.svg' },
	{ name: 'Crumbs', src: '/brand/customers/crumbs.svg' },
];

// Trustpilot reviews for the landing band (Landing2Reviews) - highlights, not
// full reviews: the reviewer's own headline plus one or two adjacent sentences
// lifted verbatim, with the whole review one click away on Trustpilot. The band
// renders nothing while this is empty, exactly like PROOF_LOGOS above. Never
// write a review here that nobody left: this section exists to survive a scam
// check, and an invented testimonial is what fails one outright.
export const TRUSTPILOT_REVIEWS_EYEBROW = 'Reviews';
export const TRUSTPILOT_REVIEWS_HEADING = 'What teams say about Naumu.';
export const TRUSTPILOT_REVIEWS: TrustpilotReview[] = [
	{
		title: 'Naumu feels like magic',
		quote:
			'There was for example a task, which naumu recognized, it checked with informations from product department, found changed requirements, which i was not aware of. This saved me some hours of work.',
		author: 'Daniel Schindler',
		stars: 5,
		date: '2026-09-02',
	},
	{
		title: 'Naumu is crazy good.',
		quote:
			"We've been using Naumu for several weeks in my org, and it's just crazy how much it improved our workflows; from always being in the loop with what the others are doing, to having an incredible context for any task that I or my agents start doing.",
		author: 'Ivo Sluganovic',
		stars: 5,
		date: '2026-08-19',
	},
	{
		title: 'An AI knowledge powerhouse.',
		quote:
			'I can finally talk to an AI which knows everything from my business and can take actions accordingly',
		author: 'Boris Klabucar',
		stars: 5,
		date: '2026-09-02',
	},
	{
		title: 'Wish I started using it sooner',
		quote:
			'After a while, naumu messaged me with a really important market update regarding a project i entered in it. Unprompted!',
		author: 'Matko Antun Bekavac',
		stars: 5,
		date: '2026-08-14',
	},
	{
		title: 'Really helps novel ideas and connections bubble up',
		quote:
			'Of all the digital second brain attempts of the past, I think Naumu finally clicked for me. All the necessary information is always right there, every insight at the right time.',
		author: 'Kristijan Kecerin',
		stars: 5,
		date: '2026-08-18',
	},
	{
		title: 'Awesome tool for messy heads',
		quote:
			'It is really impresive how slush of thoughts in your head can get a proper structure - one braindump away.',
		author: 'Kristian Djaković',
		stars: 5,
		date: '2026-08-20',
	},
	{
		title: 'Ditching Slack to switch to Naumu',
		quote:
			'The whole team is on page every day through Naumu, and we are switching the whole team from Slack there.',
		author: 'Nikola Pević',
		stars: 5,
		date: '2026-08-14',
	},
];

// Real thread (D11) — a designed mock thread rendered in the product's own chat
// surface. HONEST CAPTION ONLY: REAL_THREAD_CAPTION never claims a real user
// until Matija swaps in a consented thread (see D11).
export const REAL_THREAD_EYEBROW = 'Your new day to day';
export const REAL_THREAD_HEADING = 'A rushed update goes in. Structure comes out.';
export const REAL_THREAD_CAPTION = 'A thread like the ones users share with us.';
// The scripted exchange. Segments stay serializable (kind + strings); the
// component maps each onto its inline chat primitive, exactly like the
// use-case AskNaumuExchange.
export const REAL_THREAD_AUTHOR = 'Marcus';
export const REAL_THREAD_USER_MESSAGE =
	'before I forget - the Meridian call ran long, they want the pilot pushed to September 14th, Ana is taking onboarding, and they asked for the security doc again. Klarwerk upsell is parked until 2.1 ships.';
export const REAL_THREAD_REASONING_LABEL = 'Filing into the graph';
export const REAL_THREAD_ANSWER_SEGMENTS = [
	{ kind: 'text', text: 'Filed. Pilot for ' },
	{ kind: 'entity', text: 'Meridian' },
	{ kind: 'text', text: ' moved to ' },
	{ kind: 'entity', text: 'Sep 14' },
	{ kind: 'text', text: ', onboarding assigned to ' },
	{ kind: 'user', name: 'Ana' },
	{ kind: 'text', text: ', and I reopened ' },
	{ kind: 'mention', label: 'Security review' },
	{ kind: 'text', text: ' for them. The ' },
	{ kind: 'entity', text: 'Klarwerk' },
	{ kind: 'text', text: ' upsell is on hold until ' },
	{ kind: 'entity', text: 'v2.1' },
	{ kind: 'text', text: ' ships.' },
	// The closing line: Naumu pulls the person it just assigned work to into the
	// thread. The 'user' kind renders the badged @-mention (PersonMention), which
	// is what makes the next two beats - the join notice and Ana's reply - read
	// as consequence rather than as scripted extra messages.
	{ kind: 'text', text: ' cc ' },
	{ kind: 'user', name: 'Ana' },
	{ kind: 'text', text: ' you might wanna see this.' },
] as const;
export const REAL_THREAD_REACTION = { emoji: '🎉', count: 2 } as const;
// The system note the app posts once a mention is accepted. Kept byte-identical
// to formatMentionAddedText([{ name: 'Ana' }], false) in
// @/modules/chat/helpers/format-mention-target.ts - if that copy changes, this
// string changes with it (the fixture cannot call the helper's live path, but it
// must never invent its own wording).
export const REAL_THREAD_JOIN_NOTICE = 'Ana has joined the conversation';
// The follow-up is Ana's, not the poster's: the payoff of the cc is the person
// picking the work up, not a human marvelling at the product.
export const REAL_THREAD_FOLLOW_UP_AUTHOR = 'Ana';
export const REAL_THREAD_FOLLOW_UP = "on it - I'll send them the security doc today";

// ---------------------------------------------------------------------------
// Landing G-wave (.design/LANDING_INSPIRATION.md G1/G4/G5/G6/G10) - the
// primary "/" (cinematic Landing2Page) constants: hero baseline row, audience
// picker, changelog + blog tail, merged composer close.
// ---------------------------------------------------------------------------

// Book a demo (G4/G5). Points at the founders' Cal.com 30-minute slot - the
// same destination as config/book-demo.ts, which owns the canonical constant
// plus the new-tab link props. Off-site, so every render opens it in a new tab.
export const BOOK_A_DEMO_LABEL = 'Book a demo';
export const BOOK_A_DEMO_HREF = 'https://cal.com/naumu/demo-30-min';

// Audience picker (G6): seven pill tabs, Dev work default, each mapping to
// its existing use-case page. Claim headline + 2-3 line mechanism paragraph;
// the composite visual comes from marketing/components/fixtures (F5).
export interface AudienceTab {
	id: string;
	label: string;
	href: string;
	headline: string;
	body: string;
	/**
	 * The bare arrow link's grey tail ("<label> <linkText> ->"). Must NEVER
	 * restate the label ("Researchers for researchers") - it completes the label
	 * into a true claim the target page actually makes.
	 */
	linkText: string;
}
export const AUDIENCE_PICKER_HEADING = 'See what Naumu does with your work.';
export const AUDIENCE_TABS: readonly AudienceTab[] = [
	{
		id: 'dev-work',
		label: 'Dev work',
		href: '/use-cases/repo',
		headline: 'Your coding agents remember what the team shipped.',
		body: "One prompt wires the repo in. Before a task, every teammate's agent reads the team's memory; after it, it logs what changed and why, and closes the task.",
		// Grounded in the repo page's own lead ("Your coding agents remember what
		// your team ships") and its "Every task leaves a receipt" step.
		linkText: 'where every task leaves a receipt',
	},
	{
		id: 'client-work',
		label: 'Client work',
		href: '/use-cases/agencies',
		headline: "Every client's history, ready when they call.",
		body: 'One space per client: quotes, decisions, deadlines, files. When the client calls, the answer comes back with its source, even if the person who knew it is away.',
		// Grounded in the agencies page's own claim ("the knowing doesn't walk
		// out with the person" / "keeps walking out the door with whoever last
		// ran the account") - completes "Client work ..." like the other tails.
		linkText: "that doesn't walk out the door",
	},
	{
		id: 'sales',
		label: 'Sales',
		href: '/use-cases/sales-teams',
		headline: 'Every promise from every call, on the record.',
		body: 'Meetings become transcripts, transcripts become promises with owners and dates, and the pipeline stays current without anyone retyping it into a CRM.',
		linkText: 'calls that remember their promises',
	},
	{
		id: 'founders',
		label: 'Founders',
		href: '/use-cases/solo-builder',
		headline: 'You ship faster than you can file.',
		body: 'Keep building. Naumu keeps the record, and its agents draft the investor update from what you already said - no status meeting, no digging.',
		linkText: 'who ship faster than they can file',
	},
	{
		id: 'teams',
		label: 'Teams',
		href: '/use-cases/teams',
		headline: "Decisions stop living in someone's head.",
		body: 'Talk it out in a thread; Naumu files the decision and creates the tasks. New people read their way in instead of asking around.',
		linkText: 'keep decisions on the record',
	},
	{
		id: 'researchers',
		label: 'Researchers',
		href: '/use-cases/researcher',
		headline: 'Every answer carries its sources.',
		body: 'Notes, papers, and half-formed threads link themselves into one map. Ask a question and the answer cites the exact notes it came from.',
		linkText: 'keep a source on every answer',
	},
	{
		id: 'personal',
		label: 'Personal',
		href: '/use-cases/personal-kb',
		headline: 'Capture it now. Find it years later.',
		body: 'Paste anything - a doc, a link, a thought. It files itself by meaning, so you never decide where it goes and never lose it again.',
		linkText: 'notes that file themselves',
	},
];

// Changelog section (G10, Linear form): 4 newest releases from the SAME
// dynamic source /docs/changelog renders (getReleaseNotes() in @naumu/shared)
// - never hand-curated.
export const LANDING_CHANGELOG_EYEBROW = 'Changelog';
export const LANDING_CHANGELOG_HEADING = 'What shipped.';
export const LANDING_CHANGELOG_VIEW_ALL = 'View all';
export const LANDING_CHANGELOG_HREF = '/docs/changelog';
export const LANDING_CHANGELOG_COUNT = 4;

// Blog card row (G10): the newest three posts, same cards as the /blog index.
export const LANDING_BLOG_EYEBROW = 'From the blog';
export const LANDING_BLOG_HEADING = 'Longer reads.';
export const LANDING_BLOG_COUNT = 3;

// Merged close (G5): centered two-liner over the live composer. DRAFT COPY -
// final wording awaits the motto decision (.design/MOTTO_PANEL.md); keep the
// TAGLINE-consistent draft until that lands.
export const LANDING_CLOSE_LINE_1 = 'Start with one message.';
export const LANDING_CLOSE_LINE_2 = 'Naumu remembers the rest.';
// The one quiet honesty line by the no-signup promise: guest spaces are real
// but not eternal - say so in small muted type, one line, no asterisks.
export const LANDING_CLOSE_GUEST_EXPIRY =
	'Guest spaces expire after 30 days unless you sign in to keep them.';
// Preset intent chips under the close composer (Framer's prompt-box pattern).
// `insert` seeds the composer (the user completes the sentence); the files
// chip opens the native picker instead - its whole point is attachments.
export interface CloseChip {
	id: string;
	label: string;
	insert?: string;
	pickFiles?: boolean;
}
export const LANDING_CLOSE_CHIPS: readonly CloseChip[] = [
	{
		id: 'paste-website',
		label: 'Paste your website',
		insert: 'Here is our website - build our memory from it: ',
	},
	{
		id: 'plan-project',
		label: 'Plan a project',
		insert: "We're kicking off a project - help me plan it: ",
	},
	{ id: 'ask-files', label: 'Ask about your files', pickFiles: true },
];
export const LANDING_CLOSE_DEMO_PROMPT = 'Prefer a walkthrough?';
